import axios from "axios";
import api from "./api";

export type NewsletterBrand = "main" | "groups";

interface SubscribeParams {
  brand: NewsletterBrand;
  brandId: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

export const ENDPOINT = "website/newslettersubscription";

/**
 * POST a newsletter subscription. Throws an Error with a human-readable
 * message on failure so the caller can surface it directly to the user.
 *
 * All brands share one production backend (`api`); the `brandId` discriminates
 * the subscription so each brand's list stays separate. `brand` is kept on the
 * params as a semantic label for callers.
 */
export async function subscribeNewsletter({
  brandId,
  email,
  firstName = "",
  lastName = "",
}: SubscribeParams): Promise<void> {
  const payload = {
    companyId: process.env.NEXT_PUBLIC_COMPANY_ID ?? "",
    brandId,
    firstName,
    lastName,
    email: email.trim(),
  };

  try {
    await api.post(ENDPOINT, payload);
  } catch (err) {
    throw new Error(extractApiMessage(err));
  }
}

/**
 * Same as {@link subscribeNewsletter}, but routed through this app's own API
 * so the reCAPTCHA token can be checked against the server-side secret. Used
 * by footers that opt into protection; the direct path above is untouched for
 * the rest.
 *
 * `companyId` is deliberately omitted here — the route handler supplies it.
 */
export async function subscribeNewsletterVerified({
  brandId,
  email,
  firstName = "",
  lastName = "",
  recaptchaToken,
}: SubscribeParams & { recaptchaToken: string }): Promise<void> {
  const res = await fetch("/api/newsletter", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      brandId,
      firstName,
      lastName,
      email: email.trim(),
      recaptchaToken,
    }),
  });

  if (res.ok) return;

  // The route forwards the upstream message (e.g. "already subscribed") so the
  // toast stays as informative as it was on the direct path.
  const body = (await res.json().catch(() => null)) as { error?: string } | null;
  throw new Error(
    body?.error?.trim() || "Couldn't subscribe right now. Please try again.",
  );
}

/**
 * Pull the most useful error string out of an axios failure — server
 * `message` field if present, then HTTP status, then a generic fallback.
 */
export function extractApiMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string; errors?: unknown }
      | undefined;
    if (typeof data?.message === "string" && data.message.trim()) {
      return data.message.trim();
    }
    if (err.response?.status === 409) {
      return "This email is already subscribed.";
    }
    if (err.response?.status && err.response.status >= 500) {
      return "Our server is having a moment. Please try again shortly.";
    }
  }
  return "Couldn't subscribe right now. Please try again.";
}

/**
 * RFC-5322-light email check — same shape as the one the browser enforces
 * via `type="email"`, but we re-validate after trim() so trailing spaces
 * don't slip past native validation.
 */
export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}
